
import {
  computed, defineComponent, PropType, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import MultiSelect from 'primevue/multiselect';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import { countriesList, getCountryName } from '@/components/template-builder/utils/countries-list';
import type { RcsDedicatedFormState, RcsSelectedCarrier } from '@/types/rcs-onboarding';

/** ISO country code → flag emoji, reused from the shared countries list. */
const COUNTRY_FLAGS: Record<string, string> = countriesList.reduce(
  (acc, country) => {
    acc[country.code] = country.flag;
    return acc;
  },
  {} as Record<string, string>,
);

const countryLabel = (code: string): string => {
  const flag = COUNTRY_FLAGS[code] || '';
  return `${flag} ${getCountryName(code)}`.trim();
};

export default defineComponent({
  name: 'RcsCarrierSelectionStep',

  components: {
    Dropdown,
    MultiSelect,
    DataTable,
    Column,
  },

  props: {
    form: {
      type: Object as PropType<RcsDedicatedFormState>,
      required: true,
    },
    availableCarriers: {
      type: Array as PropType<RcsSelectedCarrier[]>,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['reload'],

  setup(props) {
    const { t } = useI18n();
    const selectedCountry = ref<string>('');

    const selectedCarriers = computed(() => props.form.carrier_selection.carriers);

    /** Distinct countries from the reference list, sorted by localized name. */
    const countryOptions = computed(() => {
      const codes = Array.from(
        new Set(props.availableCarriers.map((c) => c.country_letter_code)),
      );
      return codes
        .map((code) => ({ value: code, label: countryLabel(code) }))
        .sort((a, b) => a.label.localeCompare(b.label));
    });

    const carriersForCountry = computed(() => props.availableCarriers
      .filter((c) => c.country_letter_code === selectedCountry.value)
      .sort((a, b) => a.carrier.localeCompare(b.carrier)));

    /**
     * Carrier ids selected for the currently displayed country. Writing back
     * merges the country's picks into the global selection without dropping
     * carriers chosen for other countries.
     */
    const selectedCarrierIdsForCountry = computed<string[]>({
      get: () => selectedCarriers.value
        .filter((c) => c.country_letter_code === selectedCountry.value)
        .map((c) => c.id),
      set: (ids) => {
        const others = selectedCarriers.value.filter(
          (c) => c.country_letter_code !== selectedCountry.value,
        );
        const picked = props.availableCarriers.filter(
          (c) => c.country_letter_code === selectedCountry.value && ids.includes(c.id),
        );
        props.form.carrier_selection.carriers = [...others, ...picked];
      },
    });

    const selectedRows = computed(() => selectedCarriers.value.map((c) => ({
      ...c,
      countryLabel: countryLabel(c.country_letter_code),
    })));

    const removeCarrier = (id: string) => {
      props.form.carrier_selection.carriers = selectedCarriers.value.filter(
        (c) => c.id !== id,
      );
    };

    return {
      t,
      selectedCountry,
      countryOptions,
      carriersForCountry,
      selectedCarrierIdsForCountry,
      selectedCarriers,
      selectedRows,
      removeCarrier,
    };
  },
});
